import { useQuery } from "@tanstack/react-query";
import apiClient from "../services/apiClient";

/**
 * Statistics for a single voivodeship
 */
export interface VoivodeshipStat {
  voivodeship: string;
  name: string;
  openCalls: number;
}

/**
 * Statistics for a single program
 */
export interface ProgramStat {
  code: string;
  name: string;
  openCalls: number;
}

/**
 * Activity statistics for a single time period
 */
export interface PeriodActivityStats {
  checksPerformed: number;
  articlesDiscovered: number;
  articlesUpdated: number;
}

/**
 * Aggregated activity statistics for all time periods
 */
export interface ActivityStats {
  last24h: PeriodActivityStats;
  last7d: PeriodActivityStats;
  last30d: PeriodActivityStats;
  allTime: PeriodActivityStats;
}

/**
 * Response from GET /grants/stats endpoint
 */
export interface GrantsStatsResponse {
  totalOpenCalls: number;
  totalAnnouncedCalls: number;
  callsByVoivodeship: VoivodeshipStat[];
  callsByProgram: ProgramStat[];
  totalSources: number;
  totalArticles: number;
  articlesLast24h: number;
  callsUpdatedLast7d: number;
  generatedAt: string;
  activity?: ActivityStats;
}

/**
 * Fetches grants statistics from the public endpoint.
 * No authentication required.
 */
const fetchGrantsStats = async (): Promise<GrantsStatsResponse> => {
  const { data } = await apiClient.get("/grants/stats");
  return data;
};

/**
 * Hook for fetching grants statistics.
 * Uses React Query with 5 minute cache (shorter than backend 15 min cache).
 *
 * @example
 * const { data, isLoading, error } = useGrantsStats();
 * if (data) {
 *   console.log(`${data.totalOpenCalls} otwartych naborów`);
 * }
 */
export const useGrantsStats = () => {
  return useQuery({
    queryKey: ["grants-stats"],
    queryFn: fetchGrantsStats,
    staleTime: 5 * 60 * 1000, // 5 minutes (shorter than backend cache)
    // gcTime must be longer than backend cache TTL (15 min) to avoid
    // unnecessary refetch when user returns to the page
    gcTime: 20 * 60 * 1000, // 20 minutes garbage collection
    retry: 2,
    retryDelay: 1000,
  });
};
